import { AtsAutomationSettings } from '@/features/ats-automation-settings/ats-automation-settings'
import type { AtsDeactivation } from '@/hooks'
import { useAtsDeactivation, useJobillaOrgId } from '@/hooks'
import { useAuth, useEntitlements } from '@/lib'
import { logger } from '@/ui'
import { Button, emitUnhandledError } from '@/ui'
import { SettingsNav } from '@/ui'
import { SettingsHeading, SettingsSubheading } from '@/ui'
import { ExportCenterViewModel } from '@/ui'
import { Gate } from '@/ui/constructs/gate'
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react'
import { stripeFeature } from '@jobilla/nexus-sdk'
import { komboCreateConnectLink } from '@jobilla/nexus-sdk'
import { KomboConnectCancelledError, showKomboConnect } from '@kombo-api/connect'
import { CableIcon, TriangleAlertIcon } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SettingsStatusBox, SettingsStatusBoxProps } from '../shared/settings-status-box'

export function ExportCenter(
	{ view, loading, onConnectionComplete, onDisconnect }: ExportCenterViewModel,
) {
	const { t } = useTranslation('common')
	const jobillaOrgId = useJobillaOrgId()
	const { permissions } = useAuth()
	const { can, isLoading: entitlementsLoading } = useEntitlements()
	const deactivation = useAtsDeactivation()

	const canManage = permissions?.includes('organisation:update') ?? false

	const [isDisconnectDialogOpen, setIsDisconnectDialogOpen] = useState(false)

	async function connectAts() {
		try {
			const response = await komboCreateConnectLink(
				jobillaOrgId,
			)

			await showKomboConnect(response.data.link)

			onConnectionComplete()
		} catch (error) {
			if (error instanceof KomboConnectCancelledError) {
				return
			}

			logger.error(error, {
				tags: { category: 'export-center' },
				data: { action: 'connect-ats' },
			})
			emitUnhandledError({
				title: t('exportCenter.failedToConnectAts'),
				description: t('exportCenter.failedToConnectAtsDescription'),
				cause: error,
			})
		}
	}

	function handleDisconnect() {
		setIsDisconnectDialogOpen(false)
		onDisconnect()
	}

	let box: SettingsStatusBoxProps | null = null
	if (loading.komboStatus) {
		box = {
			status: 'pending',
			statusText: t('exportCenter.checkingStatus'),
			icon: <WaitingIcon />,
			children: (
				<div>
					{t('exportCenter.justAMoment')}
				</div>
			),
		}
	} else if (view.komboStatus === 'enabled') {
		box = {
			status: 'enabled',
			statusText: t('exportCenter.komboConnected'),
			icon: <ConnectedIcon />,
			children: (
				<div>
					<div className="mt-2 grid w-[max-content] grid-cols-[minmax(min-content,max-content)_1fr] gap-x-3 gap-y-1">
						<div className="font-medium">{t('exportCenter.connectedTo')}</div>
						<div>{view.komboToolId}</div>
						<div className="font-medium">{t('exportCenter.toolType')}</div>
						<div>{view.komboIntegrationType}</div>
					</div>
					{canManage && (
						<div className="mt-4">
							<Button type="destructive" size="short" onClick={() => setIsDisconnectDialogOpen(true)}>
								Disconnect
							</Button>
						</div>
					)}
				</div>
			),
		}
	} else if (view.komboStatus === 'disabled') {
		box = {
			status: 'disabled',
			statusText: t('exportCenter.komboNotEnabled'),
			icon: <DisconnectedIcon />,
			children: canManage ? <Button onClick={connectAts}>{t('exportCenter.connectAts')}</Button> : (
				<div>
					{t('exportCenter.askAdminToConnect')}
				</div>
			),
		}
	}

	const connection = (
		<>
			<SettingsHeading>{t('exportCenter.title')}</SettingsHeading>

			<SettingsSubheading>{t('exportCenter.kombo')}</SettingsSubheading>
			<p>
				{t('exportCenter.komboDescription')}
			</p>
			<div>
				{box && <SettingsStatusBox {...box} />}
			</div>

			<DisconnectDialog
				isOpen={isDisconnectDialogOpen}
				onClose={() => setIsDisconnectDialogOpen(false)}
				onConfirm={handleDisconnect}
			/>
		</>
	)

	// Without the add-on the page is an upgrade offer, but an integration that is still connected must stay visible
	// and disconnectable: it keeps running for 30 days, or is paused.
	const showsConnectionWithoutAddon = !entitlementsLoading && !can(stripeFeature['addon-ats-integration'])
		&& view.komboStatus === 'enabled'

	return (
		<SettingsNav active="export">
			{deactivation && <DeactivationNotice deactivation={deactivation} />}
			{showsConnectionWithoutAddon && connection}
			<Gate
				type="stripe"
				feature={stripeFeature['addon-ats-integration']}
				icon={<CableIcon className="size-7" />}
				title={t('entitlements.preview.exportCenter.title')}
				description={t('entitlements.preview.exportCenter.description')}
				benefits={[
					t('entitlements.preview.exportCenter.benefits.1'),
					t('entitlements.preview.exportCenter.benefits.2'),
					t('entitlements.preview.exportCenter.benefits.3'),
				]}
			>
				{connection}

				{canManage && !loading.komboStatus && view.komboStatus === 'enabled' && (
					<>
						<SettingsSubheading>{t('exportCenter.automation.title')}</SettingsSubheading>
						<AtsAutomationSettings />
					</>
				)}
			</Gate>
		</SettingsNav>
	)
}

function DeactivationNotice({ deactivation }: { deactivation: AtsDeactivation }) {
	const { t, i18n } = useTranslation('common')
	// The scheduled date is a whole UTC day; the local zone would show the day before west of UTC.
	const date = new Intl.DateTimeFormat(i18n.language, {
		dateStyle: 'long',
		timeZone: deactivation.kind === 'scheduled' ? 'UTC' : undefined,
	}).format(new Date(deactivation.date))

	return (
		<div role="status" className="mt-6 flex gap-3 rounded-md bg-amber-100 px-4 py-3 text-sm text-amber-900">
			<TriangleAlertIcon className="size-5 shrink-0" />
			<div>
				<p className="font-semibold">
					{deactivation.kind === 'scheduled'
						? t('exportCenter.deactivation.scheduledTitle', { date })
						: t('exportCenter.deactivation.deactivatedTitle', { date })}
				</p>
				<p className="mt-1">
					{deactivation.kind === 'scheduled'
						? t('exportCenter.deactivation.scheduledDescription')
						: t('exportCenter.deactivation.deactivatedDescription')}
				</p>
			</div>
		</div>
	)
}

function DisconnectDialog({ isOpen, onClose, onConfirm }: {
	isOpen: boolean
	onClose: () => void
	onConfirm: () => void
}) {
	return (
		<AnimatePresence mode="popLayout">
			{isOpen && (
				<Dialog static={true} open={isOpen} onClose={onClose} className="relative z-50">
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						className="fixed inset-0 bg-black/20 backdrop-blur-sm"
					/>
					<div className="fixed inset-0 w-screen overflow-y-auto">
						<div className="flex min-h-full items-center justify-center p-4">
							<DialogPanel
								as={motion.div}
								initial={{ opacity: 0, scale: 0.95, transition: { duration: 0.2 } }}
								animate={{ opacity: 1, scale: 1, transition: { duration: 0.2 } }}
								exit={{ opacity: 0, scale: 0.95, transition: { duration: 0.2 } }}
								className="w-full max-w-md rounded-xl bg-white p-6 shadow-lg ring-1 ring-grey-200/50"
							>
								<DialogTitle className="text-lg font-medium text-grey-900 mb-2">
									Disconnect ATS integration
								</DialogTitle>
								<p className="text-sm text-grey-700 mb-2">
									This will remove the integration and delete all existing campaign export mappings.
								</p>
								<p className="text-sm text-grey-600 mb-6">
									Syncing of candidates to your external ATS through Kombo will stop immediately. This
									action cannot be undone.
								</p>
								<div className="flex gap-3 justify-end">
									<Button type="subtle" size="short" onClick={onClose}>
										Cancel
									</Button>
									<Button type="destructive" size="short" onClick={onConfirm}>
										Disconnect
									</Button>
								</div>
							</DialogPanel>
						</div>
					</div>
				</Dialog>
			)}
		</AnimatePresence>
	)
}

function WaitingIcon() {
	return (
		<div className="animate-spin">
			<svg xmlns="http://www.w3.org/2000/svg" width={24} height={24} viewBox="0 0 24 24">
				<path
					d="M11.28 2.0066159999999997C9.453624 2.162736 7.872768 2.7066000000000003 6.42 3.678576C4.182528 5.175552 2.632632 7.53432 2.15064 10.176C2.024064 10.869696 2.0039759999999998 11.117064000000001 2.0044079999999997 11.976C2.0048160000000004 12.838464000000002 2.028984 13.139664 2.1506879999999997 13.8C2.451696 15.4332 3.1647600000000002 16.985447999999998 4.2040560000000005 18.27L4.393368 18.504 2.632464 18.504L0.87156 18.504 0.765768 18.559848C0.41131200000000007 18.746976 0.417744 19.254863999999998 0.7770239999999999 19.447056L0.876 19.5 3.2520000000000002 19.5L5.628 19.5 5.742528 19.43268C5.805528 19.395648 5.886528 19.32084 5.922528 19.266432000000002L5.988 19.167528 5.994648 16.789752C6.002064000000001 14.13288 6.0115680000000005 14.289096 5.832288 14.12712C5.694504 14.002632 5.51748 13.963104000000001 5.349576 14.019312000000001C5.226216 14.060616000000001 5.117616 14.151456 5.057784000000001 14.263392L5.0040000000000004 14.364 4.992 16.000464L4.98 17.636928 4.796448 17.392464C3.8454960000000002 16.125864 3.277104 14.717664 3.055776 13.08C3.0006720000000002 12.672168 2.992968 11.333088 3.043632 10.968C3.258672 9.41856 3.7074 8.195496 4.492104 7.0200000000000005C5.411208 5.643144 6.641472 4.568256 8.08212 3.883416C9.014208 3.4403040000000003 9.867552000000002 3.1938720000000003 10.992 3.043104C11.300496 3.001728 12.647784 3.001656 12.984 3.042984C14.283936 3.2027520000000003 15.465816 3.5997120000000002 16.572 4.248095999999999C16.975008000000003 4.484304 17.082768 4.504872 17.31012 4.38888C17.41308 4.336344 17.534807999999998 4.165392 17.558256 4.0404C17.57988 3.925128 17.52444 3.7267200000000003 17.445456 3.6367680000000004C17.320800000000002 3.4947840000000006 16.502976 3.0393600000000003 15.931895999999998 2.7938880000000004C15.095040000000001 2.434152 14.21268 2.193768 13.248000000000001 2.062704C12.952464 2.022552 12.723047999999999 2.010648 12.132 2.004792C11.7228 2.000712 11.339400000000001 2.0015519999999998 11.28 2.0066159999999997M18.353472 4.519848C18.225696 4.559592 18.098592 4.669224 18.045984 4.785048000000001C18.003048 4.879608 18.001056 4.989 18.001056 7.248C18.001056 9.496368 18.003216000000002 9.616776 18.045432 9.709704C18.131808 9.899856000000002 18.371112 10.029192 18.57168 9.99408C18.717456000000002 9.968592000000001 18.856368 9.873552 18.929351999999998 9.749352L18.996 9.636000000000001 19.002648 7.994496L19.009296 6.353016 19.130232 6.506495999999999C19.338383999999998 6.7707120000000005 19.737768000000003 7.389792000000001 19.926023999999998 7.74C20.683728000000002 9.149688000000001 21.032472 10.623912 20.993256000000002 12.251352C20.938776 14.51208 20.047728 16.632576 18.468 18.261C17.438232 19.32252 16.24236 20.077224 14.868 20.532984000000003C13.816248000000002 20.881752 12.871488000000001 21.021528 11.76 20.992824000000002C10.700256 20.965439999999997 9.71016 20.767319999999998 8.733936 20.38728C8.408112000000001 20.26044 7.768896000000001 19.947528000000002 7.444007999999999 19.755816C7.046112000000001 19.521 7.025304 19.512 6.880968 19.512C6.714312 19.512 6.5719199999999995 19.585872 6.471552000000001 19.724424C6.4032 19.818768000000002 6.396 19.845456000000002 6.396 20.004288C6.396 20.160912 6.403776000000001 20.190792000000002 6.468 20.280984C6.5574 20.406576 6.907128000000001 20.628696 7.461864 20.912280000000003C8.549568 21.468287999999998 9.642024 21.797736 10.908 21.951504C11.411856 22.012704 12.606528 22.012656 13.104000000000001 21.951408C14.345927999999999 21.798504 15.370704000000002 21.497808 16.428 20.976072000000002C17.74008 20.328623999999998 18.807672 19.481160000000003 19.753895999999997 18.336000000000002C20.176776 17.824224 20.566944 17.22648 20.905296 16.572C21.52092 15.381264 21.870912 14.137319999999999 21.985608 12.73236C22.021008000000002 12.298752 22.002624 11.362272 21.949800000000003 10.908C21.787464 9.511848 21.381312 8.262648 20.69352 7.0440000000000005C20.454648000000002 6.620736 20.007936 5.975664 19.709136 5.622456000000001L19.602936 5.496912 21.389232 5.490456L23.175552 5.484 23.26416 5.424C23.399256 5.332536 23.47848 5.20308 23.490888 5.05344C23.504352 4.890912 23.464512 4.770408 23.358432 4.6530000000000005C23.203248000000002 4.481231999999999 23.30844 4.487784 20.737632 4.489848C19.054896 4.491192000000001 18.41988 4.4991840000000005 18.353472 4.519848"
					stroke="none"
					fill="currentColor"
					fillRule="evenodd"
					strokeWidth={0.024}
				/>
			</svg>
		</div>
	)
}

function ConnectedIcon() {
	return (
		<>
			<svg xmlns="http://www.w3.org/2000/svg" width={24} height={24} viewBox="0 0 24 24">
				<path
					d="M11.040000000000001 0.035904C8.42748 0.257088 6.065112 1.2541920000000002 4.104024 2.963448C-0.015168000000000001 6.55368 -1.182792 12.51072 1.276704 17.388C3.326592 21.452952000000003 7.457352 24 12 24C13.889111999999999 24 15.703296 23.569944 17.388 22.722792000000002C19.507656 21.656928 21.267984000000002 19.983552 22.444584000000003 17.916C23.304456000000002 16.404984000000002 23.866968 14.548704 23.966616000000002 12.89328C23.975592 12.744384 23.992176 12.616848000000001 24.003456 12.609863999999998C24.01476 12.60288 24.023832000000002 12.314304 24.02364 11.968584C24.023424000000002 11.622864 24.014256000000003 11.354208 24.00324 11.37156C23.99148 11.390136 23.976504 11.28636 23.96688 11.11956C23.871456000000002 9.46524 23.307912 7.601088 22.444584000000003 6.0840000000000005C21.586632 4.576392 20.380488 3.236952 18.982008 2.238768C17.382792000000002 1.09728 15.546792 0.3672 13.620000000000001 0.106584C12.849120000000001 0.002304 11.782104 -0.026928000000000004 11.040000000000001 0.035904M11.172 1.0338C9.276432 1.184568 7.486992000000001 1.7991119999999998 5.916 2.8388880000000003C4.753512 3.60828 3.60828 4.753512 2.8388880000000003 5.916C1.297152 8.24544 0.69912 11.049312 1.154784 13.812000000000001C1.403136 15.317616000000001 1.9851839999999998 16.79412 2.8388880000000003 18.084C3.60828 19.246488 4.753512 20.39172 5.916 21.161112C8.245104 22.702632 11.049719999999999 23.300808 13.812000000000001 22.845216C15.806016 22.51632 17.710056 21.615528 19.224 20.284824C20.708664 18.979872 21.768384 17.402808 22.404287999999998 15.552C22.993560000000002 13.836936 23.1444 12.00192 22.845216 10.188C22.523136 8.235264 21.639696 6.343392 20.351616 4.848C20.07108 4.522320000000001 19.572984 4.021920000000001 19.224 3.715176C17.580744 2.270808 15.493416 1.3416 13.308 1.081584C12.696264 1.0087920000000001 11.75196 0.987672 11.172 1.0338M17.796 6.538752C17.7564 6.557112000000001 17.685144 6.613488 17.637648000000002 6.6640559999999995C17.590152 6.714624000000001 16.144175999999998 8.6676 14.42436 11.004C12.704544 13.3404 11.260176 15.298152 11.214624 15.354576C11.066448 15.538176 10.825488 15.608088 10.625664 15.525408000000002C10.582944000000001 15.507743999999999 9.58068 14.719848 8.398368000000001 13.774512C7.216080000000001 12.829199999999998 6.212784 12.0432 6.168792000000001 12.027864000000001C5.739816 11.87832 5.337144 12.357168 5.561808 12.749664C5.596896 12.81096 9.448224 15.921696 9.912984 16.264128C10.198416000000002 16.474439999999998 10.461864 16.563432000000002 10.798944 16.563432000000002C11.347752 16.563432000000002 11.716512 16.366775999999998 12.080952 15.879743999999999C13.250231999999999 14.317176 18.427032 7.258512 18.46272 7.178039999999999C18.55188 6.977136 18.474743999999998 6.726719999999999 18.281136 6.58848C18.198 6.529104 18.15432 6.5159519999999995 18.02448 6.511152C17.929536 6.507648 17.83968 6.518496 17.796 6.538752M0.010488 12C0.010488 12.349800000000002 0.013848 12.492912 0.017928 12.318C0.022032000000000003 12.143088 0.022032000000000003 11.856912000000001 0.017928 11.682C0.013848 11.507088 0.010488 11.6502 0.010488 12"
					stroke="none"
					fill="currentColor"
					fillRule="evenodd"
					strokeWidth={0.024}
				/>
			</svg>
		</>
	)
}

function DisconnectedIcon() {
	return (
		<>
			<svg xmlns="http://www.w3.org/2000/svg" width={24} height={24} viewBox="0 0 24 24">
				<path
					d="M1.115784 0.859248C0.997464 0.9148799999999999 0.878856 1.046256 0.8395919999999999 1.165248C0.805224 1.26936 0.8213280000000001 1.455096 0.8731679999999999 1.552704C0.8928240000000001 1.589712 5.750712 6.460272 11.668464 12.376152000000001C21.086568 21.791304 22.439976 23.135496 22.524 23.158008000000002C22.576800000000002 23.172168 22.6578 23.183544 22.704 23.183328C22.888752 23.182464000000003 23.098536 23.022264 23.160408 22.834752C23.198112 22.720512 23.176344 22.531776 23.113992 22.432344C23.087664 22.390344000000002 22.018104 21.308496 20.737224 20.028216L18.408336000000002 17.700408000000003 20.169408 15.936216C21.889560000000003 14.213016 21.931152 14.169167999999999 21.958655999999998 14.050176C21.980232 13.956743999999999 21.98016 13.900775999999999 21.958392 13.810176C21.931176 13.696944 21.869208 13.631088 20.47524 12.233976L19.02048 10.775928 20.957304 8.837976000000001C22.315056 7.479408 22.905168 6.873311999999999 22.931064 6.8107679999999995C23.096808 6.4103520000000005 22.67796 5.9914320000000005 22.277231999999998 6.156840000000001C22.214544 6.182688000000001 21.611328 6.7700640000000005 20.249976 8.130792000000001L18.311952 10.067928 16.122024 7.877976L13.932072000000002 5.688048 15.869208 3.7500240000000002C17.229936 2.388672 17.817312 1.7854560000000002 17.84316 1.7227679999999999C18.008568 1.32204 17.589648 0.9031920000000001 17.189232 1.068936C17.126687999999998 1.094832 16.520592 1.6849440000000002 15.162024 3.042696L13.224072000000001 4.97952 11.766024 3.5244240000000002C10.345992 2.1072480000000002 10.304832 2.068608 10.186176 2.041272C10.092792000000001 2.019768 10.036728 2.019864 9.946176 2.041704C9.832608 2.069064 9.759336000000001 2.138712 8.063784 3.8309280000000006L6.2995920000000005 5.591664 3.9717840000000004 3.2627760000000006C2.691504 1.9818959999999999 1.608624 0.911712 1.5654 0.8846160000000001C1.452144 0.8136240000000001 1.2385199999999998 0.801552 1.115784 0.859248M8.537976 4.770024L7.0080480000000005 6.300096000000001 12.354072000000002 11.646072L17.700096000000002 16.992048 19.230024 15.461976000000002L20.759952000000002 13.931904 15.413927999999999 8.585928000000001L10.067904 3.2399519999999997 8.537976 4.770024M5.1000000000000005 8.302512C4.930632 8.3436 4.791768 8.458728 4.733016 8.606808000000001C4.695552 8.701224 4.574736000000001 9.19692 4.514328 9.504C4.389696 10.137816 4.338888 11.014392 4.38984 11.652000000000001C4.536024 13.481520000000002 5.1850320000000005 15.118368 6.30336 16.478232L6.533472000000001 16.758024 3.856704 19.437024C1.027248 22.268832000000003 1.1282640000000002 22.160304 1.1282640000000002 22.368000000000002C1.1282640000000002 22.63272 1.36728 22.871736000000002 1.6320000000000001 22.871736000000002C1.839696 22.871736000000002 1.7311680000000003 22.972776 4.5632399999999995 20.143032L7.242504 17.466 7.50324 17.681256C8.454288 18.466368 9.526128 19.01748 10.71192 19.331088C12.242784 19.735944 13.717176 19.726848 15.263832 19.302984000000002C15.54708 19.225368 15.600312 19.202592 15.688512 19.121280000000002C15.829128 18.991656 15.880896 18.833376 15.841008 18.655079999999998C15.790056 18.427464 15.592896 18.264 15.369312 18.264C15.307368 18.264 15.1182 18.302184 14.948927999999999 18.34884C13.811520000000002 18.6624 12.59436 18.707208 11.46 18.477287999999998C9.950808 18.17136 8.680200000000001 17.490072 7.595064000000001 16.404936C6.386952 15.196824000000001 5.692632000000001 13.786536 5.432352000000001 12.012C5.37936 11.650751999999999 5.3717999999999995 10.55136 5.419872 10.200000000000001C5.470104 9.832776 5.5412159999999995 9.463416 5.617176000000001 9.1752C5.699928000000001 8.861112 5.70672 8.698608 5.642328 8.573039999999999C5.539872 8.37324 5.300544 8.253864 5.1000000000000005 8.302512"
					stroke="none"
					fill="currentColor"
					fillRule="evenodd"
					strokeWidth={0.024}
				/>
			</svg>
		</>
	)
}
